import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Button from '../common/Button'
import Icon from '../common/Icon'
import Spinner from '../common/Spinner'
import {
  RoleRef,
  UserWithRoles,
  assignRole,
  createUser,
  getRoles,
  getUsers,
  removeRole,
} from '../../api/usersService'

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/

// Pull the FastAPI error detail out of an axios error, falling back to a default.
const serverMessage = (e: unknown, fallback: string): string => {
  const detail = (e as { response?: { data?: { detail?: string } } })?.response
    ?.data?.detail
  return typeof detail === 'string' && detail ? detail : fallback
}

const InlineLoading: React.FC<{ text?: string }> = ({ text = 'Loading…' }) => (
  <div className="user-roles__loading">
    <Spinner text={text} />
  </div>
)

const UserRolesPanel: React.FC = () => {
  const [roles, setRoles] = useState<RoleRef[]>([])
  const [users, setUsers] = useState<UserWithRoles[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  // "email:roleId" keys currently being toggled, to disable just that checkbox.
  const [pending, setPending] = useState<Set<string>>(new Set())

  // Add-user form
  const [addOpen, setAddOpen] = useState(false)
  const [newEmail, setNewEmail] = useState('')
  const [newRoleIds, setNewRoleIds] = useState<Set<number>>(new Set())
  const [adding, setAdding] = useState(false)
  const [addError, setAddError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [r, u] = await Promise.all([getRoles(), getUsers()])
      setRoles(r)
      setUsers(u)
    } catch (e) {
      setError(serverMessage(e, 'Could not load users and roles.'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const toggleRole = async (
    user: UserWithRoles,
    role: RoleRef,
    hasRole: boolean
  ) => {
    const key = `${user.email}:${role.role_id}`
    setPending(prev => new Set(prev).add(key))
    setError(null)
    try {
      if (hasRole) {
        await removeRole(user.email, role.role_id)
      } else {
        await assignRole(user.email, role.role_id)
      }
      // Update local state without a full reload.
      setUsers(prev =>
        prev.map(u =>
          u.email === user.email
            ? {
                ...u,
                roles: hasRole
                  ? u.roles.filter(r => r.role_id !== role.role_id)
                  : [...u.roles, role],
              }
            : u
        )
      )
    } catch (e) {
      setError(
        serverMessage(
          e,
          `Could not ${hasRole ? 'remove' : 'assign'} ${role.name} for ${user.email}.`
        )
      )
    } finally {
      setPending(prev => {
        const next = new Set(prev)
        next.delete(key)
        return next
      })
    }
  }

  const resetAddForm = () => {
    setAddOpen(false)
    setNewEmail('')
    setNewRoleIds(new Set())
    setAddError(null)
  }

  const toggleNewRole = (roleId: number) => {
    setNewRoleIds(prev => {
      const next = new Set(prev)
      next.has(roleId) ? next.delete(roleId) : next.add(roleId)
      return next
    })
  }

  const submitAdd = async () => {
    const email = newEmail.trim().toLowerCase()
    if (!EMAIL_RE.test(email)) {
      setAddError('Enter a valid email address.')
      return
    }
    setAdding(true)
    setAddError(null)
    try {
      const created = await createUser(email, Array.from(newRoleIds))
      // Drop the new user straight into the list (sorted by email like the backend).
      setUsers(prev =>
        [...prev.filter(u => u.email.toLowerCase() !== email), created].sort(
          (a, b) => a.email.localeCompare(b.email)
        )
      )
      resetAddForm()
    } catch (e) {
      setAddError(serverMessage(e, 'Could not add the user.'))
    } finally {
      setAdding(false)
    }
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return users
    return users.filter(u => u.email.toLowerCase().includes(q))
  }, [users, search])

  if (loading) {
    return <InlineLoading />
  }

  return (
    <div className="user-roles">
      <div className="user-roles__header">
        <div>
          <h3 className="user-roles__title">Users &amp; Roles</h3>
          <p className="user-roles__subtitle">
            Assign or remove BreadGPT roles. Changes take effect on the user’s
            next sign-in.
          </p>
        </div>
        {!addOpen && (
          <Button
            variant="primary"
            className="user-roles__add-btn"
            onClick={() => setAddOpen(true)}
          >
            <Icon name="new-chat" size={16} /> Add user
          </Button>
        )}
      </div>

      {error && <div className="user-roles__error">{error}</div>}

      {addOpen && (
        <div className="user-roles__add">
          <label className="user-roles__add-field">
            <span className="user-roles__label">Email</span>
            <span className="user-roles__hint">
              Must be the user’s exact Microsoft sign-in (UPN) — otherwise it
              won’t match when they log in.
            </span>
            <input
              className="user-roles__input"
              type="email"
              placeholder="someone@breadltd.co.uk"
              value={newEmail}
              autoFocus
              onChange={e => setNewEmail(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !adding) submitAdd()
              }}
            />
          </label>

          <div className="user-roles__add-field">
            <span className="user-roles__label">Roles</span>
            <div className="user-roles__add-roles">
              {roles.map(role => (
                <label key={role.role_id} className="user-roles__add-role">
                  <input
                    type="checkbox"
                    className="user-roles__checkbox"
                    checked={newRoleIds.has(role.role_id)}
                    onChange={() => toggleNewRole(role.role_id)}
                  />
                  {role.name}
                </label>
              ))}
            </div>
          </div>

          {addError && <div className="user-roles__error">{addError}</div>}

          <div className="user-roles__add-actions">
            <Button variant="secondary" onClick={resetAddForm} disabled={adding}>
              Cancel
            </Button>
            <Button variant="primary" onClick={submitAdd} isLoading={adding}>
              Add user
            </Button>
          </div>
        </div>
      )}

      <input
        className="user-roles__search"
        placeholder="Search by email…"
        value={search}
        onChange={e => setSearch(e.target.value)}
      />

      <div className="user-roles__table">
        <div className="user-roles__row user-roles__row--head">
          <span className="user-roles__cell user-roles__cell--email">User</span>
          {roles.map(role => (
            <span
              key={role.role_id}
              className="user-roles__cell user-roles__cell--role"
              title={role.description || undefined}
            >
              {role.name}
            </span>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div className="user-roles__empty">No users match “{search}”.</div>
        ) : (
          filtered.map(user => (
            <div key={user.email} className="user-roles__row">
              <span className="user-roles__cell user-roles__cell--email">
                {user.email}
              </span>
              {roles.map(role => {
                const hasRole = user.roles.some(
                  r => r.role_id === role.role_id
                )
                const key = `${user.email}:${role.role_id}`
                const busy = pending.has(key)
                return (
                  <span
                    key={role.role_id}
                    className="user-roles__cell user-roles__cell--role"
                  >
                    <input
                      type="checkbox"
                      className="user-roles__checkbox"
                      checked={hasRole}
                      disabled={busy}
                      onChange={() => toggleRole(user, role, hasRole)}
                      aria-label={`${role.name} for ${user.email}`}
                    />
                  </span>
                )
              })}
            </div>
          ))
        )}
      </div>
    </div>
  )
}

export default UserRolesPanel
